import { useEffect, useRef, useState } from "react";
import { signInAnonymously } from "firebase/auth";
import { auth } from "../lib/firebase";
import { useAuth } from "./useAuth";
import { LoadingState } from "../components/ui/spinner";

// GuestRoute ensures *some* Firebase user exists on a link-shared poll route.
// A logged-out visitor is silently signed in anonymously (once, ref-guarded);
// a real signed-in user passes straight through. Per C4 the layout switch
// (AppLayout vs GuestLayout) lives in the consumer (SchedulerShell), keyed on
// `user.isAnonymous` — this route only guarantees a user is present.
export default function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  const signingInRef = useRef(false);
  const [sessionError, setSessionError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (user || loading || sessionError) return;
    if (signingInRef.current) return;
    signingInRef.current = true;
    signInAnonymously(auth)
      .catch((err) => {
        console.error("Failed to start guest session:", String(err));
        setSessionError(true);
      })
      .finally(() => {
        signingInRef.current = false;
      });
  }, [user, loading, sessionError, retryNonce]);

  if (sessionError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center text-slate-700 dark:text-slate-200">
        <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Couldn&apos;t start guest session — try again.
        </p>
        <button
          type="button"
          onClick={() => {
            setSessionError(false);
            setRetryNonce((n) => n + 1);
          }}
          className="min-h-[44px] rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center dark:text-slate-300">
        <LoadingState message="Starting guest session..." />
      </div>
    );
  }

  return children;
}
